/**
 * PaintedScene — abstract base for the five hand-painted journey scenes.
 *
 * Generalises the mechanics that TitleScene currently bakes inline: a stack
 * of painted background layers, an animated Waker walker constrained to a
 * polygon, edge zones that fire scene transitions, and interaction hotspots
 * that show a "Press E" prompt and emit a custom event.
 *
 * Each concrete scene supplies:
 *   - A Phaser scene key (e.g. "HearthScene") and a manifest scene key
 *     (e.g. "scene-2-hearth"). The two are kept separate so Phaser-side
 *     bookkeeping (registration, restart) and authoring identity stay
 *     decoupled.
 *   - Either an inline `PaintedSceneAssetSpec` via `provideAssetSpec()` (for
 *     placeholder scenes) or a JSON spec at the manifest's `assetPath`.
 *   - Optionally, the journey manifest. Subclasses can override
 *     `provideManifest()` to supply a manifest in tests / placeholders that
 *     do not boot from `journey-manifest.json`.
 *
 * Acceptance for AGE-1419: the base must compile under strict TS, must not
 * break the existing TitleScene flow, and must support a placeholder concrete
 * scene wired through the same pipeline. Music/audio hooks are exposed as
 * overridable methods so authoring can plug in per-scene beds without the
 * base class touching the audio pipeline directly.
 */

import * as Phaser from 'phaser';
import {
  type JourneyManifest,
  type PaintedSceneSpec,
  type EdgeZone,
  type InteractionHotspot,
  type NormalisedPoint,
  type EpicCamera,
  type MaterialZone,
  type DayNightProfile,
} from './journeyManifest.schema';
import {
  zoneContains,
  interactionContains,
  interactionCentre,
} from './paintedSceneGeometry';
import {
  type PaintedSceneAssetSpec,
  validatePaintedSceneAssetSpec,
  samplePerspectiveScale,
  yToDepthBracket,
} from './sceneAssetSpec.schema';
import { ParallaxLayerManager, type LayerRuntime } from './ParallaxLayerManager';
import { OrganicMotionSystem } from './OrganicMotionSystem';
import { WalkablePolygon } from './WalkablePolygon';
import { PolygonPathfinder, type PathPoint } from './PolygonPathfinder';
import { loadSettings, saveSettings } from '../../ui/SettingsPersistence';
import {
  HudEvents,
  emitHintChanged,
  emitSceneLabelChanged,
  emitTimeTick,
} from '../../game/HudState';
import { resolveMusicUrls, resolveRegionKey } from '../../audio/paintedSceneMusic';
import { PaintedSceneAudioManager, type PaintedTerrainType } from '../../audio/PaintedSceneAudioManager';
import { TimeSystem, type TimeTickPayload } from '../../systems/TimeSystem';
import { parseHint } from '../../ui/c5/hintText';
import { TimeOfDayColorGrader } from '../../rendering/TimeOfDayColorGrader';
import { PaintedSceneFireflies } from './PaintedSceneFireflies';
import { NatureScatterSystem } from './NatureScatterSystem';
import { type TimeOfDay, DAY_DURATION_MS } from '@game/shared';

// ── Walker tuning constants ─────────────────────────────────────────────────

/**
 * Walker movement speed in normalised units / second.
 *
 * Was 0.06, which is ~58px/s on the 960px canvas — about seventeen seconds to
 * cross the scene. It also starved the walk cycle: the rig takes its stride
 * amplitude from actual velocity, so at that crawl the legs barely swung and
 * the character shuffled. Raised toward the hand-rolled game's feel (~0.10
 * normalised/s), with WalkerRig.STRIDE raised alongside so the cadence stays
 * near 1.3 gait cycles a second rather than windmilling.
 */
const WALKER_SPEED_NORM = 0.115;

/** Click-to-move speed multiplier (lets follow paths feel a little brisker). */
const CLICK_MOVE_MULT = 1.2;

/** Walker rendered depth (above midground, below foreground). */
const WALKER_DEPTH = 100;
const WALKER_SHADOW_DEPTH = 99;
const PROMPT_DEPTH = 200;
const DEBUG_OVERLAY_DEPTH = 500;

/** Painterly serif used for prompts / debug labels. */
const PROMPT_FONT = "'Almendra', serif";
const COLOR_INK = '#eae0c8';

/** Camera fade duration for naive scene transitions. */
const TRANSITION_FADE_MS = 700;

/** Key badge shown on the C5 hint slip for the interact affordance. */
const INTERACT_KEY_LABEL = 'E';

/** Re-publish the day/night clock onto the HudState bus at ~4 Hz (C5 frame). */
const HUD_TIME_BRIDGE_INTERVAL_MS = 250;

// ── Per-scene runtime state ─────────────────────────────────────────────────

// LayerRuntime is now imported from ParallaxLayerManager

interface InteractionRuntime {
  spec: InteractionHotspot;
  promptText: Phaser.GameObjects.Text | null;
  /** True while the walker is inside the hotspot region. */
  isInside: boolean;
  /** True after the interaction's flag has fired (for non-repeatable ones). */
  hasFired: boolean;
}

/** Init payload accepted via `scene.start()`. */
export interface PaintedSceneInitData {
  /** Override the spawn point — used by edge-zone arrivals. */
  spawn?: NormalisedPoint;
  /** Source scene key — debug / analytics. */
  fromScene?: string;
}

/** Event payloads emitted by PaintedScene for other systems to consume. */
export interface EdgeZoneEnteredEvent {
  sceneKey: string;
  zone: EdgeZone;
}
export interface InteractionTriggeredEvent {
  sceneKey: string;
  interaction: InteractionHotspot;
}

export const PAINTED_SCENE_EVENTS = {
  EDGE_ZONE_ENTERED: 'painted-scene:edge-zone-entered',
  INTERACTION_TRIGGERED: 'painted-scene:interaction-triggered',
  TRANSITION_REQUESTED: 'painted-scene:transition-requested',
  JOURNAL_REQUESTED: 'painted-scene:journal-requested',
  READY: 'painted-scene:ready',
} as const;

/**
 * SMA-42 Phase 2 — registry flag the PaintedOverworldShell sets when it is
 * hosting painted scenes through SceneRouter. When set, the per-scene soft
 * fallback in `requestTransition` is skipped so the shell's
 * `NetworkSceneTransitionRunner` is the only thing that drives the swap
 * (and the swap waits for the server's `MSG_ZONE_ENTER` confirmation).
 */
export const PAINTED_SHELL_MANAGED_REGISTRY_KEY = 'painted-shell:managed';

// ── Base class ──────────────────────────────────────────────────────────────

export abstract class PaintedScene extends Phaser.Scene {
  /** Manifest scene-key (e.g. "scene-2-hearth"). Public so the transition
   *  runner (AGE-1421) can match a Phaser scene to a manifest entry. */
  public readonly manifestKey: string;

  /** Resolved manifest entry. Public read so the transition runner can pull
   *  music keys / spawn / edge-zone metadata without re-parsing. */
  public manifestEntry: PaintedSceneSpec | null = null;

  /** Validated asset spec. Public read for the transition runner. */
  public assetSpec: PaintedSceneAssetSpec | null = null;
  polygon: WalkablePolygon | null = null;
  pathfinder: PolygonPathfinder | null = null;

  protected layerManager: ParallaxLayerManager | null = null;
  protected get layers(): LayerRuntime[] {
    return this.layerManager?.layers ?? [];
  }
  protected interactions: InteractionRuntime[] = [];

  protected walker: Phaser.GameObjects.Sprite | null = null;
  protected walkerShadow: Phaser.GameObjects.Ellipse | null = null;
  protected walkerNX = 0.5;
  protected walkerNY = 0.5;
  protected walkerNZ = 0;

  /**
   * Width of the world in px. Normalised x spans this, not the viewport, so a
   * scene can be wider than the screen and the camera can travel across it.
   */
  protected worldWidth(): number {
    return this.scale.width * (this.assetSpec?.worldWidthFactor ?? 1);
  }
  protected walkerCurrentAnim = '';
  protected walkerPrevNX = 0.5;
  protected walkerPrevNY = 0.5;

  /** Current depth bracket for the walker (bracket-based scenes only). */
  private walkerBracket = -1;
  /**
   * Depth the walker sits at right now.
   *
   * In bracket mode this comes from sortByBracket and is well above the fixed
   * WALKER_DEPTH — bracket 2 lands at 299 while the mid-ground painting sits at
   * 103. applyWalkerDepth used to reset to WALKER_DEPTH unconditionally every
   * frame, which stomped that and drew the background over the character.
   */
  private walkerBaseDepth = WALKER_DEPTH;

  /** Click-to-move follow path in normalised coords. */
  private clickPath: PathPoint[] | null = null;
  private clickPathProgress = 0;
  /**
   * Total length of the current click path in normalised units.
   *
   * `clickPathProgress` is a 0..1 fraction of the whole path, so advancing it
   * at a fixed rate made every click-to-walk take the same wall-clock time no
   * matter how far it went — a single step and a full traverse both took about
   * seven seconds. Dividing the per-frame step by this length turns that
   * fraction back into a constant speed.
   */
  private clickPathLength = 0;

  /** Edge zones that triggered most recently — used for re-entry hysteresis. */
  private lastZoneId: string | null = null;
  /** Suppress edge zones targeting this scene until the walker leaves them.
   *  Prevents instant bounce-back when spawning inside a return edge zone. */
  private suppressedFromScene: string | null = null;
  private interactionPressed = false;

  /** Scene music (preloaded + looped via Phaser sound manager). */
  private sceneMusic: Phaser.Sound.BaseSound | null = null;
  private sceneMusicRegionKey: string | null = null;

  /** Per-scene ambient audio + footstep SFX manager. */
  private sceneAudio: PaintedSceneAudioManager | null = null;

  /**
   * Set by a subclass whose character rig emits real foot-contact events, so
   * the wall-clock footstep timer stops competing with them. Without this the
   * scene plays a step every 350ms while the legs land on their own schedule,
   * and the two drift audibly against each other.
   */
  protected footstepsDrivenByRig = false;

  /** Organic ambient motion (sway/breathing) for painted layers. */
  private organicMotion: OrganicMotionSystem | null = null;

  /** Scattered nature elements (wildflowers, rocks, grass). */
  private natureScatter: NatureScatterSystem | null = null;

  /** Day/night cycle systems. */
  private timeSystem: TimeSystem | null = null;
  /** Accumulator for the ~4 Hz HudState time bridge. */
  private hudTimeBridgeMs = 0;
  private colorGrader: TimeOfDayColorGrader | null = null;
  private fireflies: PaintedSceneFireflies | null = null;
  private dayNightEnabled = true;
  private dayNightProfile: DayNightProfile | null = null;

  /** Guards against double-firing transitions during fade-out. */
  private transitionActive = false;

  /** Debug overlay state. */
  private debugOverlayVisible = false;
  private debugGraphics: Phaser.GameObjects.Graphics | null = null;
  private debugLabels: Phaser.GameObjects.Text[] = [];

  /** Resolved epic camera config for this scene (null = normal scene). */
  private epicCamera: EpicCamera | null = null;

  /** Scene fully built and walker spawned. Public so the transition runner
   *  (AGE-1421) can wait for the target scene to be paintable before fading. */
  public ready = false;

  /** Init data from `scene.start(...)`. */
  private initData: PaintedSceneInitData = {};

  constructor(phaserSceneKey: string, manifestKey: string) {
    super({ key: phaserSceneKey });
    this.manifestKey = manifestKey;
  }

  // ── overridable hooks ────────────────────────────────────────────────────

  /**
   * Subclass hook returning an inline asset spec. Return null (default) to
   * load the spec from the manifest's `assetPath` over HTTP.
   */
  protected provideAssetSpec(): PaintedSceneAssetSpec | null {
    return null;
  }

  /**
   * Subclass hook returning a manifest. Return null (default) to read the
   * manifest from the Phaser JSON cache under `journey-manifest`.
   */
  protected provideManifest(): JourneyManifest | null {
    return null;
  }

  /** Hook fired after a non-repeatable interaction's flag is set. */
  protected onInteractionTriggered(_interaction: InteractionHotspot): void {
    /* override in subclass */
  }

  /** Hook fired when the walker enters an edge zone. */
  protected onEdgeZoneEntered(_zone: EdgeZone): void {
    /* override in subclass */
  }

  /** Hook fired right before scene shutdown — cleanup audio etc. */
  /**
   * Where in the day this scene's clock starts, 0..1. Null (the default) seeds
   * from the wall clock, which is right for a world you pass through. A scene
   * that is a single run overrides it — The Small Hours opens at dusk because
   * the game is the night, and a player who loaded in at noon would have to
   * wait thirteen in-game hours for it to begin.
   */
  protected dayNightSeedFraction(): number | null {
    return null;
  }

  /**
   * Stop the clock. For a scene that is a single run rather than a place you
   * pass through: when the run is over, the sky must stop, or the morning it
   * arrived at quietly becomes the next afternoon and then the next dusk, and
   * the ending is overwritten by the world carrying on.
   */
  protected dayNightFrozen = false;

  protected onSceneShutdown(): void {
    /* override in subclass */
  }

  /** Hook fired once the scene is fully built and the walker has spawned. */
  protected onSceneReady(): void {
    /* override in subclass */
  }

  // ── lifecycle ────────────────────────────────────────────────────────────

  init(data?: PaintedSceneInitData): void {
    this.initData = data ?? {};
    this.ready = false;
    this.lastZoneId = null;
    this.suppressedFromScene = this.initData.fromScene ?? null;
    this.layerManager = null;
    this.interactions = [];
    this.walker = null;
    this.walkerShadow = null;
    this.clickPath = null;
    this.clickPathProgress = 0;
    this.sceneMusic = null;
    this.sceneMusicRegionKey = null;
    this.sceneAudio = null;
    this.organicMotion = null;
    this.natureScatter = null;
    this.transitionActive = false;
    this.epicCamera = null;
    this.walkerBracket = -1;
    this.debugOverlayVisible = false;
    this.debugGraphics = null;
    this.debugLabels = [];
    this.timeSystem = null;
    this.colorGrader = null;
    this.fireflies = null;
    this.dayNightEnabled = true;
    this.dayNightProfile = null;
  }

  preload(): void {
    this.preloadWakerSpritesheets();
    this.preloadSceneMusic();

    const inline = this.provideAssetSpec();
    if (inline) {
      this.assetSpec = inline;
      this.preloadLayerImages(inline);
    } else {
      const entry = this.resolveManifestEntry();
      const specCacheKey = `spec:${this.manifestKey}`;
      this.load.json(specCacheKey, entry.assetPath);
      this.load.on(`filecomplete-json-${specCacheKey}`, () => {
        const raw = this.cache.json.get(specCacheKey);
        if (raw) {
          const spec = validatePaintedSceneAssetSpec(raw);
          this.assetSpec = spec;
          this.preloadLayerImages(spec);
        }
      });
    }
  }

  private ensureLayerManager(): ParallaxLayerManager {
    if (!this.layerManager) {
      this.layerManager = new ParallaxLayerManager(this, this.manifestKey);
    }
    return this.layerManager;
  }

  private preloadLayerImages(spec: PaintedSceneAssetSpec): void {
    this.ensureLayerManager().preloadLayerImages(spec);
  }

  create(): void {
    const manifestEntry = this.resolveManifestEntry();
    this.manifestEntry = manifestEntry;

    const spec = this.assetSpec ?? this.loadSpecFromCache();
    this.assetSpec = spec;

    if (spec.key !== this.manifestKey) {
      throw new Error(
        `PaintedScene[${this.manifestKey}]: asset spec key "${spec.key}" does not match manifest key`,
      );
    }

    this.cameras.main.setBackgroundColor('#1a1a2e');

    this.polygon = new WalkablePolygon(spec.walkablePolygon);
    this.pathfinder = new PolygonPathfinder(this.polygon);

    const mgr = this.ensureLayerManager();
    mgr.buildLayers(spec);
    this.buildWalker(manifestEntry, spec);
    this.applyInitialBracketDepths(spec);
    this.buildInteractions(manifestEntry);

    this.bindKeyboard();
    this.bindClickToMove();
    this.bindShutdown();

    this.applyEpicCamera(manifestEntry);

    this.mountTravellingCamera();
    this.cameras.main.fadeIn(TRANSITION_FADE_MS, 0, 0, 0);

    // Off unless explicitly enabled — the walk polygon, hotspot boxes and
    // edge-zone labels were the first thing a player saw on a plain load.
    if (loadSettings().showDebugOverlay === true) {
      this.debugOverlayVisible = true;
      this.drawDebugOverlay();
    }

    this.game.events.on(HudEvents.DEBUG_OVERLAY_CHANGED, this.onDebugOverlayChanged, this);

    this.organicMotion = new OrganicMotionSystem();

    if (spec.nature && this.polygon) {
      this.natureScatter = new NatureScatterSystem(this);
      this.natureScatter.scatter(spec, this.polygon, mgr);
    }

    this.organicMotion.start(mgr.layers);

    this.bootDayNightCycle(manifestEntry);

    // C5 frame: the top band's specimen label is the scene's display name.
    emitSceneLabelChanged(this.game, manifestEntry.displayName);

    this.ready = true;
    this.events.emit(PAINTED_SCENE_EVENTS.READY, { sceneKey: this.manifestKey });
    this.startSceneMusic();
    this.startSceneAmbient(manifestEntry);
    this.onSceneReady();
  }

  update(_time: number, delta: number): void {
    if (!this.ready) return;
    if (!this.dayNightFrozen) this.timeSystem?.update(delta);
    this.tickHudTimeBridge(delta);
    this.tickAmbient(_time);
    this.organicMotion?.tick(_time);
    this.tickWalker(delta);
    this.tickDepthBrackets();
    this.tickEdgeZones();
    this.tickInteractions();
  }

  // ── manifest / spec resolution ───────────────────────────────────────────

  private resolveManifestEntry(): PaintedSceneSpec {
    const manifest = this.provideManifest() ?? this.readManifestFromCache();
    const entry = manifest.scenes.find(s => s.key === this.manifestKey);
    if (!entry) {
      throw new Error(
        `PaintedScene: manifest has no scene with key "${this.manifestKey}"`,
      );
    }
    return entry;
  }

  private readManifestFromCache(): JourneyManifest {
    const raw = this.cache.json.get('journey-manifest');
    if (!raw) {
      throw new Error(
        `PaintedScene[${this.manifestKey}]: journey-manifest not in cache and no inline manifest provided`,
      );
    }
    return raw as JourneyManifest;
  }

  private loadSpecFromCache(): PaintedSceneAssetSpec {
    const raw = this.cache.json.get(`spec:${this.manifestKey}`);
    if (!raw) {
      throw new Error(
        `PaintedScene[${this.manifestKey}]: asset spec not in cache (preload must run first)`,
      );
    }
    return validatePaintedSceneAssetSpec(raw);
  }

  // ── layers (delegated to ParallaxLayerManager) ───────────────────────────

  private tickAmbient(timeMs: number): void {
    this.layerManager?.tickAmbient(timeMs);
  }

  private applyInitialBracketDepths(spec: PaintedSceneAssetSpec): void {
    if (!this.layerManager?.hasBrackets) return;
    const bracket = this.computeWalkerBracket(spec);
    this.walkerBracket = bracket;
    const { walkerDepth, shadowDepth } = this.layerManager.sortByBracket(bracket);
    this.walkerBaseDepth = walkerDepth;
    if (this.walker) this.walker.setDepth(walkerDepth);
    if (this.walkerShadow) this.walkerShadow.setDepth(shadowDepth);
  }

  private computeWalkerBracket(spec: PaintedSceneAssetSpec): number {
    if (spec.depthBracketThresholds && spec.depthBracketThresholds.length > 0) {
      return yToDepthBracket(this.walkerNY, spec.depthBracketThresholds);
    }
    return spec.walkerDepthBracket ?? 0;
  }

  private tickDepthBrackets(): void {
    if (!this.layerManager?.hasBrackets || !this.assetSpec) return;
    const bracket = this.computeWalkerBracket(this.assetSpec);
    if (bracket === this.walkerBracket) return;
    this.walkerBracket = bracket;
    const { walkerDepth, shadowDepth } = this.layerManager.sortByBracket(bracket);
    this.walkerBaseDepth = walkerDepth;
    if (this.walker) this.walker.setDepth(walkerDepth);
    if (this.walkerShadow) this.walkerShadow.setDepth(shadowDepth);
  }

  // ── walker ───────────────────────────────────────────────────────────────

  /** Default Waker spritesheet preload. Subclasses can override to skip. */
  protected preloadWakerSpritesheets(): void {
    if (this.textures.exists('waker-walk-up')) return;
    const cfg = { frameWidth: 128, frameHeight: 128 };
    this.load.spritesheet('waker-walk-up', 'assets/sprites/waker/waker-walk-up.png', cfg);
    this.load.spritesheet('waker-walk-down', 'assets/sprites/waker/waker-walk-down.png', cfg);
    this.load.spritesheet('waker-walk-left', 'assets/sprites/waker/waker-walk-left.png', cfg);
    this.load.spritesheet('waker-idle', 'assets/sprites/waker/waker-idle.png', cfg);
  }

  // ── scene music ─────────────────────────────────────────────────────────

  private preloadSceneMusic(): void {
    const entry = this.resolveManifestEntry();
    const regionKey = resolveRegionKey(entry.music);
    if (this.cache.audio.exists(regionKey)) return;
    const urls = resolveMusicUrls(entry.music);
    this.load.audio(regionKey, urls);
  }

  private static readonly ACTIVE_MUSIC_KEY = 'painted:active-music-region';
  private static readonly ACTIVE_MUSIC_SOUND_KEY = 'painted:active-music-sound';

  private startSceneMusic(): void {
    const entry = this.manifestEntry;
    if (!entry) return;
    const regionKey = resolveRegionKey(entry.music);
    this.sceneMusicRegionKey = regionKey;

    const activeRegion = this.game.registry.get(PaintedScene.ACTIVE_MUSIC_KEY) as string | undefined;
    const activeSound = this.game.registry.get(PaintedScene.ACTIVE_MUSIC_SOUND_KEY) as Phaser.Sound.BaseSound | undefined;

    if (activeRegion === regionKey && activeSound) {
      const snd = activeSound as unknown as { isPlaying: boolean; pendingRemove: boolean };
      if (snd.isPlaying && !snd.pendingRemove) {
        this.sceneMusic = activeSound;
        return;
      }
    }

    if (activeSound && activeRegion !== regionKey) {
      try { activeSound.stop(); } catch { /* already stopped */ }
      try { activeSound.destroy(); } catch { /* already destroyed */ }
    }

    if (!this.cache.audio.exists(regionKey)) return;

    const settings = loadSettings();
    const volume = settings.masterVolume * settings.musicVolume;
    const muted = this.game.registry.get('app:audioMuted') === true;

    this.sceneMusic = this.sound.add(regionKey, {
      loop: true,
      volume,
      mute: muted,
    });
    this.sceneMusic.play();

    this.game.registry.set(PaintedScene.ACTIVE_MUSIC_KEY, regionKey);
    this.game.registry.set(PaintedScene.ACTIVE_MUSIC_SOUND_KEY, this.sceneMusic);
  }

  private stopSceneMusic(): void {
    this.sceneMusic = null;
    this.sceneMusicRegionKey = null;
  }

  private startSceneAmbient(entry: PaintedSceneSpec): void {
    if (!entry.ambient) return;
    this.sceneAudio = new PaintedSceneAudioManager();
    if (this.epicCamera && this.epicCamera.walkerScale < 1) {
      this.sceneAudio.setWalkerScale(this.epicCamera.walkerScale);
    }
    this.sceneAudio.setAmbient(entry.ambient);
  }

  private stopSceneAmbient(): void {
    if (this.sceneAudio) {
      this.sceneAudio.destroy();
      this.sceneAudio = null;
    }
  }

  /** Register walker animations once. Idempotent across scene restarts. */
  protected ensureWalkerAnimations(): void {
    if (this.anims.exists('painted-walker-idle')) return;
    const fpsWalk = 8;
    if (this.textures.exists('waker-walk-up')) {
      this.anims.create({
        key: 'painted-walker-walk-up',
        frames: this.anims.generateFrameNumbers('waker-walk-up', { start: 0, end: 11 }),
        frameRate: 15,
        repeat: -1,
      });
    }
    if (this.textures.exists('waker-walk-down')) {
      this.anims.create({
        key: 'painted-walker-walk-down',
        frames: this.anims.generateFrameNumbers('waker-walk-down', { start: 0, end: 11 }),
        frameRate: 15,
        repeat: -1,
      });
    }
    if (this.textures.exists('waker-walk-left')) {
      this.anims.create({
        key: 'painted-walker-walk-left',
        frames: this.anims.generateFrameNumbers('waker-walk-left', { start: 0, end: 5 }),
        frameRate: 10,
        repeat: -1,
      });
    }
    if (this.textures.exists('waker-idle')) {
      this.anims.create({
        key: 'painted-walker-idle',
        frames: this.anims.generateFrameNumbers('waker-idle', { start: 0, end: 7 }),
        frameRate: 15,
        repeat: -1,
      });
    }
  }

  private buildWalker(manifest: PaintedSceneSpec, _spec: PaintedSceneAssetSpec): void {
    this.ensureWalkerAnimations();

    const spawn = this.initData.spawn ?? manifest.defaultSpawn;
    this.walkerNX = spawn.x;
    this.walkerNY = spawn.y;
    this.walkerPrevNX = spawn.x;
    this.walkerPrevNY = spawn.y;
    if (this.polygon?.hasZData) {
      this.walkerNZ = this.polygon.interpolateZ(spawn.x, spawn.y);
    }

    const w = this.worldWidth();
    const h = this.scale.height;

    if (this.textures.exists('waker-walk-up')) {
      const sprite = this.add.sprite(spawn.x * w, spawn.y * h, 'waker-walk-up', 0);
      sprite.setOrigin(0.5, 1);
      sprite.setDepth(WALKER_DEPTH);
      this.walker = sprite;

      const shadow = this.add.ellipse(spawn.x * w, spawn.y * h, 28, 10, 0x000000, 0.3);
      shadow.setDepth(WALKER_SHADOW_DEPTH);
      this.walkerShadow = shadow;

      if (this.anims.exists('painted-walker-idle')) {
        sprite.play('painted-walker-idle');
        this.walkerCurrentAnim = 'painted-walker-idle';
      }
      this.applyWalkerScale();
    }
  }

  /** Play a footstep on a real foot contact. Called by a subclass's rig. */
  protected triggerFootstep(): void {
    this.sceneAudio?.playFootstepNow();
  }

  /** Summed segment length of a path, in normalised units. */
  private static pathLength(path: PathPoint[]): number {
    let total = 0;
    for (let i = 1; i < path.length; i++) {
      total += Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
    }
    return total;
  }

  private applyWalkerScale(): void {
    if (!this.walker || !this.assetSpec) return;
    const nz = this.walkerNZ || undefined;
    let scale = samplePerspectiveScale(this.walkerNY, this.assetSpec.perspective, nz);
    if (this.epicCamera) {
      scale *= this.epicCamera.walkerScale;
    }
    this.walker.setScale(scale);
    if (this.walkerShadow) {
      this.walkerShadow.setScale(scale * 0.9, scale * 0.45);
    }
    this.applyWalkerDepth();
  }

  private applyWalkerDepth(): void {
    if (!this.walker) return;
    const base = this.walkerBaseDepth;
    if (this.walkerNZ <= 0) {
      this.walker.setDepth(base);
      if (this.walkerShadow) this.walkerShadow.setDepth(base - 1);
      return;
    }
    // Pseudo-Z lifts the walker within its own bracket, never out of it.
    const zDepth = base - Math.min(50, Math.round(this.walkerNZ * 50));
    this.walker.setDepth(zDepth);
    if (this.walkerShadow) this.walkerShadow.setDepth(zDepth - 1);
  }

  /** Apply epic camera overrides: zoom, parallax amplification, entry pan. */
  /**
   * Bound the camera to the world and let it follow the walker.
   *
   * Only when the world is actually wider than the viewport — a single-screen
   * scene keeps its fixed camera, and a following camera there would just
   * jitter against the bounds. The deadzone gives the player room to move
   * before the world starts sliding, so small steps do not swim the whole
   * frame; the lerp keeps the catch-up soft rather than glued.
   */
  private mountTravellingCamera(): void {
    const worldW = this.worldWidth();
    const viewW = this.scale.width;
    if (worldW <= viewW + 1) return;

    const cam = this.cameras.main;
    cam.setBounds(0, 0, worldW, this.scale.height);
    if (this.walker) {
      cam.startFollow(this.walker, true, 0.08, 0.08);
      cam.setDeadzone(viewW * 0.34, this.scale.height);
    }
  }

  private applyEpicCamera(entry: PaintedSceneSpec): void {
    if (!entry.epicCamera) return;
    const epic = entry.epicCamera;
    this.epicCamera = epic;

    // Apply zoomed-out default and compensate layer sizes so they still fill
    // the visible area (camera zoom < 1 reveals more world space).
    this.cameras.main.setZoom(epic.defaultZoom);
    this.ensureLayerManager().scaleForZoom(epic.defaultZoom);

    // Amplify parallax spread on layers
    if (epic.parallaxAmplify > 1) {
      for (const layer of this.layers) {
        const base = layer.spec.parallax;
        if (base < 1 && layer.spec.ambient?.kind === 'parallax-drift') {
          const amp = layer.spec.ambient.amplitude * epic.parallaxAmplify;
          (layer.spec.ambient as { amplitude: number }).amplitude = amp;
        }
      }
    }

    // Entry pan tween
    if (epic.entryPan) {
      const pan = epic.entryPan;
      if (pan.firstVisitOnly) {
        const flag = `epic-pan-seen:${entry.key}`;
        const seen = this.game.registry.get(flag);
        if (seen) return;
        this.game.registry.set(flag, true);
      }
      const cam = this.cameras.main;
      const [dx, dy] = pan.offsetPx;
      cam.scrollX += dx;
      cam.scrollY += dy;
      this.tweens.add({
        targets: cam,
        scrollX: cam.scrollX - dx,
        scrollY: cam.scrollY - dy,
        duration: pan.durationMs,
        ease: pan.easing === 'easeOutCubic' ? 'Cubic.easeOut' : 'Cubic.easeInOut',
      });
    }
  }

  private bindKeyboard(): void {
    if (!this.input.keyboard) return;
    const cursors = this.input.keyboard.createCursorKeys();
    const keys = this.input.keyboard.addKeys('W,A,S,D,E') as {
      W: Phaser.Input.Keyboard.Key;
      A: Phaser.Input.Keyboard.Key;
      S: Phaser.Input.Keyboard.Key;
      D: Phaser.Input.Keyboard.Key;
      E: Phaser.Input.Keyboard.Key;
    };
    this.data.set('painted-cursors', cursors);
    this.data.set('painted-keys', keys);

    this.input.keyboard.on('keydown-BACKTICK', () => {
      this.toggleDebugOverlay();
      const settings = loadSettings();
      saveSettings({ ...settings, showDebugOverlay: this.debugOverlayVisible });
      this.game.events.emit(HudEvents.DEBUG_OVERLAY_CHANGED, this.debugOverlayVisible);
    });

    this.input.keyboard.on('keydown-J', () => {
      this.game.events.emit(PAINTED_SCENE_EVENTS.JOURNAL_REQUESTED);
    });
  }

  private bindClickToMove(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.ready || !this.pathfinder || !this.polygon) return;
      // A subclass gets first refusal, so a click on something in the world can
      // choose its own destination — the approach point beside a log rather
      // than the log itself, which is usually outside the walkable polygon and
      // would path nowhere at all.
      if (this.onSceneClick(pointer)) return;
      const p = this.pointerWorld(pointer);
      this.walkTo(p.x / this.worldWidth(), p.y / this.scale.height);
    });
  }

  /**
   * Where a click landed in the world.
   *
   * Explicitly projected through the main camera rather than read off
   * `pointer.worldX`. A scene with a second full-viewport camera — the grove's
   * unfiltered light camera, for one — hands Phaser a choice about which
   * camera a pointer belongs to, and `pointer.camera` does resolve to that
   * second camera. Measured, `worldX` still came back as the main camera's
   * projection, so this is not a bug being fixed; it is a dependency on
   * undocumented resolution order being removed. The scene knows which camera
   * it means, so it should say so.
   */
  protected pointerWorld(pointer: Phaser.Input.Pointer): { x: number; y: number } {
    const p = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    return { x: p.x, y: p.y };
  }

  /**
   * Handle a click before click-to-move does. Return true to consume it.
   * Default: not handled.
   */
  protected onSceneClick(_pointer: Phaser.Input.Pointer): boolean {
    return false;
  }

  /**
   * Path the walker to a normalised point. Returns false when no route exists.
   * The one entry point for walking — click-to-move and any subclass-driven
   * errand both go through it, so speed and arrival behave identically.
   */
  protected walkTo(nx: number, ny: number): boolean {
    if (!this.pathfinder || !this.polygon) return false;
    const start = { x: this.walkerNX, y: this.walkerNY };
    const path = this.pathfinder.findPath(start, { x: nx, y: ny });
    if (!path || path.length < 2) {
      this.clickPath = null;
      return false;
    }
    this.clickPath = PolygonPathfinder.simplifyPath(path);
    this.clickPathProgress = 0;
    this.clickPathLength = PaintedScene.pathLength(this.clickPath);
    return true;
  }

  /** True while the walker is following a click path. */
  protected isWalking(): boolean {
    return this.clickPath !== null;
  }

  private bindShutdown(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.ready = false;
      this.tearDownDayNightCycle();
      this.stopSceneMusic();
      this.stopSceneAmbient();
      if (this.natureScatter) {
        this.natureScatter.destroy();
        this.natureScatter = null;
      }
      if (this.organicMotion) {
        this.organicMotion.destroy();
        this.organicMotion = null;
      }
      this.game.events.off(HudEvents.DEBUG_OVERLAY_CHANGED, this.onDebugOverlayChanged, this);
      emitSceneLabelChanged(this.game, null);
      emitHintChanged(this.game, null);
      this.clearDebugOverlay();
      this.onSceneShutdown();
    });
  }

  private tickWalker(deltaMs: number): void {
    if (!this.walker || !this.polygon) return;
    const cursors = this.data.get('painted-cursors') as Phaser.Types.Input.Keyboard.CursorKeys | undefined;
    const keys = this.data.get('painted-keys') as
      | { W: Phaser.Input.Keyboard.Key; A: Phaser.Input.Keyboard.Key; S: Phaser.Input.Keyboard.Key; D: Phaser.Input.Keyboard.Key; E: Phaser.Input.Keyboard.Key }
      | undefined;

    let dx = 0;
    let dy = 0;
    let moved = false;
    const step = WALKER_SPEED_NORM * (deltaMs / 1000);

    const wDown = keys?.W.isDown || cursors?.up?.isDown;
    const sDown = keys?.S.isDown || cursors?.down?.isDown;
    const aDown = keys?.A.isDown || cursors?.left?.isDown;
    const dDown = keys?.D.isDown || cursors?.right?.isDown;

    if (wDown || sDown || aDown || dDown) {
      this.clickPath = null;
      if (wDown) dy -= step;
      if (sDown) dy += step;
      if (aDown) dx -= step;
      if (dDown) dx += step;
      if (dx !== 0 && dy !== 0) {
        const len = Math.hypot(dx, dy);
        dx = (dx / len) * step;
        dy = (dy / len) * step;
      }
      moved = true;
    } else if (this.clickPath && this.clickPath.length > 1) {
      // Speed is units/second, so the fraction has to advance in inverse
      // proportion to how long the path actually is.
      this.clickPathProgress += (step * CLICK_MOVE_MULT) / Math.max(this.clickPathLength, 1e-4);
      const seg = this.clickPathProgress * (this.clickPath.length - 1);
      const i = Math.min(Math.floor(seg), this.clickPath.length - 2);
      const frac = seg - i;
      const a = this.clickPath[i];
      const b = this.clickPath[i + 1];
      const nx = a.x + (b.x - a.x) * frac;
      const ny = a.y + (b.y - a.y) * frac;
      const az = a.z ?? 0;
      const bz = b.z ?? 0;
      this.walkerNZ = az + (bz - az) * frac;
      dx = nx - this.walkerNX;
      dy = ny - this.walkerNY;
      this.walkerNX = nx;
      this.walkerNY = ny;
      moved = true;
      if (this.clickPathProgress >= 1) {
        const last = this.clickPath[this.clickPath.length - 1];
        this.walkerNX = last.x;
        this.walkerNY = last.y;
        this.walkerNZ = last.z ?? 0;
        this.clickPath = null;
      }
    }

    if (moved && (dx !== 0 || dy !== 0) && this.clickPath === null) {
      const candX = this.walkerNX + dx;
      const candY = this.walkerNY + dy;
      if (this.polygon.contains(candX, candY)) {
        this.walkerNX = candX;
        this.walkerNY = candY;
      } else {
        const clamp = this.polygon.closestPointInside(candX, candY);
        this.walkerNX = clamp.x;
        this.walkerNY = clamp.y;
      }
      if (this.polygon.hasZData) {
        this.walkerNZ = this.polygon.interpolateZ(this.walkerNX, this.walkerNY);
      }
    }

    const w = this.worldWidth();
    const h = this.scale.height;
    this.walker.setPosition(this.walkerNX * w, this.walkerNY * h);
    this.applyWalkerScale();

    if (this.walkerShadow) {
      this.walkerShadow.setPosition(this.walker.x, this.walker.y);
    }

    if (moved && (dx !== 0 || dy !== 0)) {
      this.updateWalkAnimation(dx, dy);
      this.walkerPrevNX = this.walkerNX;
      this.walkerPrevNY = this.walkerNY;
      this.updateTerrainFromPosition();
      if (!this.footstepsDrivenByRig) {
        this.sceneAudio?.playFootstep(performance.now());
      }
    } else if (this.walkerCurrentAnim !== 'painted-walker-idle' && this.anims.exists('painted-walker-idle')) {
      this.walker.play('painted-walker-idle');
      this.walkerCurrentAnim = 'painted-walker-idle';
    }
  }

  private updateWalkAnimation(dx: number, dy: number): void {
    if (!this.walker) return;
    let anim: string;
    let flipX = false;
    if (Math.abs(dx) > Math.abs(dy) * 0.6) {
      if (dx < 0) {
        anim = 'painted-walker-walk-left';
        flipX = false;
      } else {
        anim = 'painted-walker-walk-left';
        flipX = true;
      }
    } else if (dy < 0) {
      anim = 'painted-walker-walk-up';
    } else {
      anim = 'painted-walker-walk-down';
    }
    if (anim !== this.walkerCurrentAnim && this.anims.exists(anim)) {
      this.walker.play(anim);
      this.walkerCurrentAnim = anim;
    }
    this.walker.flipX = flipX;
  }

  private updateTerrainFromPosition(): void {
    if (!this.sceneAudio || !this.manifestEntry) return;
    const zones = this.manifestEntry.materialZones;
    if (!zones || zones.length === 0) return;
    for (const zone of zones) {
      if (zoneContains(zone, this.walkerNX, this.walkerNY)) {
        this.sceneAudio.setTerrain(zone.material as PaintedTerrainType);
        return;
      }
    }
    this.sceneAudio.setTerrain('grass');
  }

  // ── edge zones ───────────────────────────────────────────────────────────

  private tickEdgeZones(): void {
    if (!this.manifestEntry) return;
    let activeId: string | null = null;
    for (const zone of this.manifestEntry.edgeZones) {
      if (zoneContains(zone, this.walkerNX, this.walkerNY)) {
        activeId = zone.id;
        if (this.suppressedFromScene && zone.targetScene === this.suppressedFromScene) {
          break;
        }
        if (this.lastZoneId !== zone.id) {
          this.lastZoneId = zone.id;
          this.handleEdgeZoneEnter(zone);
        }
        break;
      }
    }
    if (activeId === null) {
      this.lastZoneId = null;
      this.suppressedFromScene = null;
    }
  }

  private handleEdgeZoneEnter(zone: EdgeZone): void {
    const payload: EdgeZoneEnteredEvent = { sceneKey: this.manifestKey, zone };
    this.events.emit(PAINTED_SCENE_EVENTS.EDGE_ZONE_ENTERED, payload);
    this.game.events.emit(PAINTED_SCENE_EVENTS.EDGE_ZONE_ENTERED, payload);
    this.onEdgeZoneEntered(zone);
    this.requestTransition(zone);
  }

  /**
   * Default transition: emit a transition-requested event for an external
   * router (`SceneRouter`) to handle, and as a soft fallback do a camera
   * fade + `scene.start` in case nothing is listening.
   *
   * Subclasses can override to customise the transition, but the default
   * keeps the placeholder scene self-contained for the AGE-1419 acceptance.
   */
  protected requestTransition(zone: EdgeZone): void {
    if (this.transitionActive) return;

    const payload = {
      fromScene: this.manifestKey,
      toScene: zone.targetScene,
      kind: zone.transitionKind,
      spawn: zone.spawn,
      durationMs: zone.durationMs,
    };
    this.game.events.emit(PAINTED_SCENE_EVENTS.TRANSITION_REQUESTED, payload);

    // SMA-42 Phase 2 — when the PaintedOverworldShell is hosting, defer the
    // entire swap to its NetworkSceneTransitionRunner so transitions only
    // happen after the server has confirmed the zone change. Skipping the
    // soft fallback here prevents an off-by-one scene swap that would
    // otherwise race the server's MSG_ZONE_ENTER reply.
    if (this.game.registry.get(PAINTED_SHELL_MANAGED_REGISTRY_KEY) === true) {
      return;
    }

    // Soft fallback: if no router intercepts, drive the swap directly.
    // Uses scene.launch + stop (parallel) instead of scene.start (sequential)
    // so the target scene's music can reference-count the shared track before
    // the source scene releases it.
    const targetPhaserKey = this.resolvePhaserSceneKeyForManifest(zone.targetScene);
    if (!targetPhaserKey || targetPhaserKey === this.scene.key) return;
    if (!this.scene.get(targetPhaserKey)) return;

    this.transitionActive = true;

    this.cameras.main.fadeOut(zone.durationMs ?? TRANSITION_FADE_MS, 0, 0, 0);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      const initData: PaintedSceneInitData = {
        spawn: zone.spawn,
        fromScene: this.manifestKey,
      };
      const sourceKey = this.scene.key;

      this.scene.launch(targetPhaserKey, initData);
      const target = this.scene.get(targetPhaserKey);

      const finishTransition = (): void => {
        this.game.scene.stop(sourceKey);
      };

      if (target) {
        let settled = false;
        const settle = (): void => {
          if (settled) return;
          settled = true;
          target.events.off(PAINTED_SCENE_EVENTS.READY, settle);
          finishTransition();
        };
        target.events.once(PAINTED_SCENE_EVENTS.READY, settle);
        this.time.delayedCall(3000, settle);
      } else {
        finishTransition();
      }
    });
  }

  /**
   * Walk the registered scene list looking for a Phaser scene whose
   * `manifestKey` matches. Default implementation looks at every scene in the
   * SceneManager and tests whether it inherits from PaintedScene with a
   * matching manifestKey. Subclasses can override for a faster lookup table.
   */
  protected resolvePhaserSceneKeyForManifest(manifestKey: string): string | null {
    const scenes = this.scene.manager.scenes;
    for (const s of scenes) {
      if (s instanceof PaintedScene && s.manifestKey === manifestKey) {
        return s.scene.key;
      }
    }
    return null;
  }

  // ── interaction hotspots ─────────────────────────────────────────────────

  private buildInteractions(manifest: PaintedSceneSpec): void {
    for (const interaction of manifest.interactions) {
      this.interactions.push({
        spec: interaction,
        promptText: null,
        isInside: false,
        hasFired: false,
      });
    }
  }

  private tickInteractions(): void {
    const keys = this.data.get('painted-keys') as
      | { E: Phaser.Input.Keyboard.Key } | undefined;
    const ePressed = keys ? Phaser.Input.Keyboard.JustDown(keys.E) : false;

    for (const ix of this.interactions) {
      const inside = interactionContains(ix.spec, this.walkerNX, this.walkerNY);
      if (inside && !ix.isInside) {
        this.showPrompt(ix);
        // C5 frame: the same affordance, on paper, under the brass clip.
        emitHintChanged(this.game, parseHint(ix.spec.prompt, INTERACT_KEY_LABEL));
      } else if (!inside && ix.isInside) {
        ix.promptText?.setVisible(false);
        emitHintChanged(this.game, null);
      }
      ix.isInside = inside;
      if (inside && ePressed) {
        if (!ix.spec.repeatable && ix.hasFired) continue;
        ix.hasFired = true;
        const payload: InteractionTriggeredEvent = {
          sceneKey: this.manifestKey,
          interaction: ix.spec,
        };
        this.events.emit(PAINTED_SCENE_EVENTS.INTERACTION_TRIGGERED, payload);
        this.game.events.emit(PAINTED_SCENE_EVENTS.INTERACTION_TRIGGERED, payload);
        this.onInteractionTriggered(ix.spec);
      }
    }
    this.interactionPressed = ePressed;
  }

  private showPrompt(ix: InteractionRuntime): void {
    const w = this.worldWidth();
    const h = this.scale.height;
    const anchor = ix.spec.anchor ?? interactionCentre(ix.spec);
    if (!ix.promptText) {
      ix.promptText = this.add.text(anchor.x * w, anchor.y * h, ix.spec.prompt, {
        fontFamily: PROMPT_FONT,
        fontSize: '14px',
        color: COLOR_INK,
        shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 3, fill: true },
      }).setOrigin(0.5, 1).setDepth(PROMPT_DEPTH);
    } else {
      ix.promptText.setPosition(anchor.x * w, anchor.y * h);
      ix.promptText.setVisible(true);
    }
  }

  // ── HUD time bridge (C5 Layer 0) ─────────────────────────────────────────
  //
  // TimeSystem lives per painted scene and emits on `scene.events`, which the
  // DOM frame cannot see. Re-publish it on the game-wide HudState bus at
  // ~4 Hz: fast enough for the day-wheel to read as continuous motion, slow
  // enough to cost nothing. (TimeSystem's own TICK is 1 Hz — too steppy for a
  // rotating disc, so we sample the clock directly.)

  private tickHudTimeBridge(delta: number): void {
    const ts = this.timeSystem;
    if (!ts) return;
    this.hudTimeBridgeMs += delta;
    if (this.hudTimeBridgeMs < HUD_TIME_BRIDGE_INTERVAL_MS) return;
    this.hudTimeBridgeMs = 0;
    emitTimeTick(this.game, {
      dayTimeMs: ts.getDayTimeMs(),
      fraction: ts.getFraction(),
      phase: ts.getPhase(),
      moonPhase: ts.getMoonPhase(),
    });
  }

  // ── day/night cycle ──────────────────────────────────────────────────────

  private bootDayNightCycle(entry: PaintedSceneSpec): void {
    const profile = entry.dayNight;
    this.dayNightProfile = profile ?? null;
    this.dayNightEnabled = profile?.enabled !== false;

    if (!this.dayNightEnabled) return;

    this.timeSystem = new TimeSystem(this);
    // Seed from wall-clock so all clients start at a consistent time — unless
    // the scene is a *run* with its own arc, in which case arriving at a random
    // hour of a 20-minute world day is the wrong opening entirely.
    const seed = this.dayNightSeedFraction();
    const seedMs = seed === null
      ? Date.now() % DAY_DURATION_MS
      : Phaser.Math.Clamp(seed, 0, 0.9999) * DAY_DURATION_MS;
    this.timeSystem.syncFromServer({
      dayTimeMs: seedMs,
      serverTimestampMs: Date.now(),
    });

    this.colorGrader = new TimeOfDayColorGrader(this);
    this.colorGrader.attachToCamera(this.cameras.main);

    this.fireflies = new PaintedSceneFireflies(this);

    this.events.on(TimeSystem.EVENTS.TICK, this.onDayNightTick, this);
    this.events.on(TimeSystem.EVENTS.PHASE_CHANGE, this.onDayNightPhaseChange, this);

    // Apply initial state immediately
    const initialPhase = this.timeSystem.getPhase();
    this.onDayNightPhaseChange({ from: initialPhase, to: initialPhase });
  }

  private tearDownDayNightCycle(): void {
    if (this.timeSystem) {
      this.events.off(TimeSystem.EVENTS.TICK, this.onDayNightTick, this);
      this.events.off(TimeSystem.EVENTS.PHASE_CHANGE, this.onDayNightPhaseChange, this);
      this.timeSystem.destroy();
      this.timeSystem = null;
    }
    if (this.colorGrader) {
      this.colorGrader.destroy();
      this.colorGrader = null;
    }
    if (this.fireflies) {
      this.fireflies.destroy();
      this.fireflies = null;
    }
  }

  private onDayNightTick(payload: TimeTickPayload): void {
    this.colorGrader?.onTimeTick(payload);
    this.layerManager?.applyTimeOfDayTint(payload.ambient);
  }

  private onDayNightPhaseChange(ev: { from: TimeOfDay; to: TimeOfDay }): void {
    const phase = ev.to;

    // Fireflies: active during dusk and night
    if (this.fireflies) {
      if (phase === 'dusk' || phase === 'night') {
        this.fireflies.activate();
      } else {
        this.fireflies.deactivate();
      }
    }

    // Phase-aware ambient audio
    if (this.sceneAudio && this.manifestEntry?.ambient) {
      const phaseProfile = this.dayNightProfile?.[phase];
      this.sceneAudio.setPhaseAmbient(phaseProfile?.ambient, this.manifestEntry.ambient);
    }
  }

  // ── debug overlay ─────────────────────────────────────────────────────────

  private onDebugOverlayChanged(visible: boolean): void {
    if (visible === this.debugOverlayVisible) return;
    this.debugOverlayVisible = visible;
    if (visible) {
      this.drawDebugOverlay();
    } else {
      this.clearDebugOverlay();
    }
  }

  private toggleDebugOverlay(): void {
    this.debugOverlayVisible = !this.debugOverlayVisible;
    if (this.debugOverlayVisible) {
      this.drawDebugOverlay();
    } else {
      this.clearDebugOverlay();
    }
  }

  private clearDebugOverlay(): void {
    if (this.debugGraphics) {
      this.debugGraphics.destroy();
      this.debugGraphics = null;
    }
    for (const label of this.debugLabels) {
      label.destroy();
    }
    this.debugLabels = [];
  }

  private drawDebugOverlay(): void {
    this.clearDebugOverlay();
    if (!this.manifestEntry) return;

    const w = this.worldWidth();
    const h = this.scale.height;
    const g = this.add.graphics();
    g.setDepth(DEBUG_OVERLAY_DEPTH);
    this.debugGraphics = g;

    // Walkable polygon
    if (this.polygon) {
      const verts = this.assetSpec?.walkablePolygon;
      if (verts && verts.length >= 3) {
        g.lineStyle(2, 0x00ff00, 0.6);
        g.beginPath();
        g.moveTo(verts[0].x * w, verts[0].y * h);
        for (let i = 1; i < verts.length; i++) {
          g.lineTo(verts[i].x * w, verts[i].y * h);
        }
        g.closePath();
        g.strokePath();
      }
    }

    // Edge zones
    for (const zone of this.manifestEntry.edgeZones) {
      const color = this.edgeZoneColor(zone.transitionKind);
      this.drawZoneShape(g, zone, w, h, color, 0.25);
      const centre = this.zoneCentre(zone);
      const label = this.add.text(centre.x * w, centre.y * h, `→ ${zone.targetScene}\n[${zone.transitionKind}]`, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#ffffff',
        backgroundColor: '#00000088',
        padding: { x: 3, y: 2 },
        align: 'center',
      }).setOrigin(0.5, 0.5).setDepth(DEBUG_OVERLAY_DEPTH + 1);
      this.debugLabels.push(label);
    }

    // Interaction hotspots
    for (const ix of this.manifestEntry.interactions) {
      this.drawZoneShape(g, ix, w, h, 0xffaa00, 0.2);
      const centre = interactionCentre(ix);
      const label = this.add.text(centre.x * w, centre.y * h - 10, `⚡ ${ix.id}\n${ix.prompt}`, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#ffdd88',
        backgroundColor: '#00000088',
        padding: { x: 3, y: 2 },
        align: 'center',
      }).setOrigin(0.5, 1).setDepth(DEBUG_OVERLAY_DEPTH + 1);
      this.debugLabels.push(label);
    }
  }

  private drawZoneShape(
    g: Phaser.GameObjects.Graphics,
    zone: { rect?: { x: number; y: number; w: number; h: number }; polygon?: ReadonlyArray<{ x: number; y: number }> },
    w: number,
    h: number,
    color: number,
    fillAlpha: number,
  ): void {
    g.fillStyle(color, fillAlpha);
    g.lineStyle(2, color, 0.8);
    if (zone.rect) {
      const r = zone.rect;
      g.fillRect(r.x * w, r.y * h, r.w * w, r.h * h);
      g.strokeRect(r.x * w, r.y * h, r.w * w, r.h * h);
    } else if (zone.polygon && zone.polygon.length >= 3) {
      const pts = zone.polygon;
      g.beginPath();
      g.moveTo(pts[0].x * w, pts[0].y * h);
      for (let i = 1; i < pts.length; i++) {
        g.lineTo(pts[i].x * w, pts[i].y * h);
      }
      g.closePath();
      g.fillPath();
      g.strokePath();
    }
  }

  private zoneCentre(zone: { rect?: { x: number; y: number; w: number; h: number }; polygon?: ReadonlyArray<{ x: number; y: number }> }): { x: number; y: number } {
    if (zone.rect) {
      return { x: zone.rect.x + zone.rect.w / 2, y: zone.rect.y + zone.rect.h / 2 };
    }
    if (zone.polygon && zone.polygon.length > 0) {
      let sx = 0, sy = 0;
      for (const p of zone.polygon) { sx += p.x; sy += p.y; }
      return { x: sx / zone.polygon.length, y: sy / zone.polygon.length };
    }
    return { x: 0.5, y: 0.5 };
  }

  private edgeZoneColor(kind: string): number {
    switch (kind) {
      case 'edge-crossfade': return 0x4488ff;
      case 'doorway': return 0xff4488;
      case 'threshold-zoom': return 0xff8844;
      case 'camera-pan': return 0x44ff88;
      case 'edge-pan': return 0x88ff44;
      case 'wash': return 0xaa44ff;
      default: return 0xffffff;
    }
  }
}

export { zoneContains, interactionContains, interactionCentre } from './paintedSceneGeometry';
export { ParallaxLayerManager, type LayerRuntime } from './ParallaxLayerManager';
export { OrganicMotionSystem } from './OrganicMotionSystem';
export { PaintedSceneFireflies } from './PaintedSceneFireflies';
export { NatureScatterSystem } from './NatureScatterSystem';

/**
 * Type-narrowing helper for the AGE-1421 transition runner: returns true iff
 * `scene` is a fully built `PaintedScene` with its walker in place. Avoids
 * the runner having to import PaintedScene directly when the only thing it
 * cares about is whether the target is paintable yet.
 */
export function isPaintedSceneReady(scene: Phaser.Scene): boolean {
  return scene instanceof PaintedScene && scene.ready;
}
